import { ICustomFieldDefinition } from "../../../../@types/admin/AdminHospitalDetails";
import { useDeactivateCustomField } from "../../../../hooks/mutations/admin/hospitalDetails";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../../shared";

interface IDeactivateCustomFieldModalProps {
  field: ICustomFieldDefinition;
  hospitalGroupId: number;
  onDone: () => void;
}

/**
 * Deactivates a custom-field definition (EN-5175).
 *
 * ⚠️ The route is a DELETE but the effect is a deactivation: the answers already
 * recorded against the field stay, and it can be reactivated. The dialog says
 * so, because "Delete" on a field with patient answers behind it is exactly the
 * kind of button an operator hesitates over — or worse, doesn't.
 */
export function DeactivateCustomFieldModal({
  field,
  hospitalGroupId,
  onDone,
}: IDeactivateCustomFieldModalProps) {
  const { showToast } = useToast();
  const { deactivateCustomField, isDeactivatingCustomField } =
    useDeactivateCustomField();

  const handleDeactivate = async () => {
    try {
      await deactivateCustomField({ id: field.id, hospitalGroupId });
      showToast(`${field.label} was deactivated.`, "success");
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error deactivating this custom field."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Deactivate custom field</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Deactivate <strong>{field.label}</strong> ({field.key})?
        </ModalBodyText>
        <WarningNotice>
          It stops appearing on any ward&apos;s forms. The answers already
          recorded against it are kept, and it can be reactivated from this list
          — nothing is deleted.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isDeactivatingCustomField}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isDeactivatingCustomField}
            onClick={handleDeactivate}
          >
            {isDeactivatingCustomField ? "Deactivating..." : "Deactivate"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
